import { Contact, ContactAssetSummary, ContactChannel, ContactChannelType, ContactMetadata, LanguageOption, MetadataOption } from '../types/Contact';
import { withResolvedContactName } from '../contactNameUtils';
import { apiRequest } from '../../../shared/services/apiClient';

export interface ContactChannelRequest {
    id?: string;
    type: ContactChannelType;
    label?: string;
    value: string;
    isPrimary: boolean;
}

export interface CreateContactRequest {
    name?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    title?: string;
    phoneCountryCode?: string;
    phoneNumber?: string;
    whatsApp?: string;
    status?: string;
    marketingEmails?: boolean;
    language?: string;
    reminderPreference?: string;
    ubn?: string;
    accountingId?: string;
    channels?: ContactChannelRequest[];
}

export interface UpdateContactRequest {
    name?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    title?: string;
    phoneCountryCode?: string;
    phoneNumber?: string;
    whatsApp?: string;
    status?: string;
    marketingEmails?: boolean;
    language?: string;
    reminderPreference?: string;
    ubn?: string;
    accountingId?: string;
    channels?: ContactChannelRequest[];
}

const contactChannelTypes: ContactChannelType[] = ['phone', 'whatsApp', 'instagram', 'telegram', 'messenger', 'tikTok', 'signal', 'other'];

const fromApiChannelType = (value: unknown): ContactChannelType => {
    switch (String(value ?? '').toLowerCase()) {
        case 'phone':
            return 'phone';
        case 'whatsapp':
            return 'whatsApp';
        case 'instagram':
            return 'instagram';
        case 'telegram':
            return 'telegram';
        case 'messenger':
            return 'messenger';
        case 'tiktok':
            return 'tikTok';
        case 'signal':
            return 'signal';
        default:
            return 'other';
    }
};

const toApiChannelType = (value: ContactChannelType): string => {
    switch (value) {
        case 'phone':
            return 'Phone';
        case 'whatsApp':
            return 'WhatsApp';
        case 'instagram':
            return 'Instagram';
        case 'telegram':
            return 'Telegram';
        case 'messenger':
            return 'Messenger';
        case 'tikTok':
            return 'TikTok';
        case 'signal':
            return 'Signal';
        default:
            return 'Other';
    }
};

const isContactChannelType = (value: unknown): value is ContactChannelType => typeof value === 'string' && contactChannelTypes.includes(value as ContactChannelType);

const normalizeChannel = (value: any): ContactChannel => ({
    id: value?.id ? String(value.id) : undefined,
    type: isContactChannelType(value?.type) ? value.type : fromApiChannelType(value?.type),
    label: String(value?.label ?? value?.value ?? ''),
    value: String(value?.value ?? ''),
    link: typeof value?.link === 'string' && value.link.trim().length > 0 ? value.link.trim() : undefined,
    isPrimary: Boolean(value?.isPrimary),
});

const normalizeLanguageOption = (value: any): LanguageOption => ({
    code: String(value?.code ?? ''),
    label: String(value?.label ?? value?.code ?? ''),
});

const normalizeContactAssetSummary = (value: any): ContactAssetSummary => ({
    id: String(value?.id ?? ''),
    name: String(value?.name ?? ''),
    assetType: String(value?.assetType ?? value?.species ?? ''),
    identifier: value?.identifier ? String(value.identifier) : undefined,
});

const normalizeMetadataOption = (value: any): MetadataOption => ({
    value: String(value?.value ?? ''),
    label: String(value?.label ?? value?.value ?? ''),
});

const pickPrimaryValue = (channels: ContactChannel[], type: ContactChannelType): string => {
    return channels.find(channel => channel.type === type && channel.isPrimary)?.value
        ?? channels.find(channel => channel.type === type)?.value
        ?? '';
};

const normalizeContact = (value: any): Contact => ({
    channels: Array.isArray(value?.channels) ? value.channels.map(normalizeChannel) : [],
    id: String(value?.id ?? ''),
    name: String(value?.name ?? ''),
    firstName: value?.firstName ? String(value.firstName) : undefined,
    lastName: value?.lastName ? String(value.lastName) : undefined,
    email: String(value?.email ?? ''),
    title: value?.title ? String(value.title) : undefined,
    phoneCountryCode: value?.phoneCountryCode ? String(value.phoneCountryCode) : undefined,
    phone: String(value?.phone ?? ''),
    whatsApp: String(value?.whatsApp ?? value?.whatsapp ?? ''),
    status: value?.status ? String(value.status) : undefined,
    marketingEmails: Boolean(value?.marketingEmails),
    language: value?.language ? String(value.language) : undefined,
    reminderPreference: value?.reminderPreference ? String(value.reminderPreference) : undefined,
    ubn: value?.ubn ? String(value.ubn) : undefined,
    accountingId: value?.accountingId ? String(value.accountingId) : undefined,
    createdAt: value?.createdAt ? String(value.createdAt) : undefined,
    updatedAt: value?.updatedAt ? String(value.updatedAt) : undefined,
    assetCount: Number(value?.assetCount ?? 0),
    assets: Array.isArray(value?.assets) ? value.assets.map(normalizeContactAssetSummary) : [],
});

const normalizeContactWithDerivedChannels = (value: any): Contact => {
    const contact = normalizeContact(value);
    return withResolvedContactName({
        ...contact,
        phone: String(value?.phone ?? pickPrimaryValue(contact.channels, 'phone') ?? ''),
        whatsApp: String(value?.whatsApp ?? value?.whatsapp ?? pickPrimaryValue(contact.channels, 'whatsApp') ?? ''),
    });
};

const normalizeContactMetadata = (value: any): ContactMetadata => ({
    statuses: Array.isArray(value?.statuses) ? value.statuses.map(normalizeMetadataOption) : [],
    reminderPreferences: Array.isArray(value?.reminderPreferences) ? value.reminderPreferences.map(normalizeMetadataOption) : [],
    languages: Array.isArray(value?.languages) ? value.languages.map(normalizeLanguageOption) : [],
});

const pickItems = (payload: any): any[] => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.items)) return payload.items;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.data?.items)) return payload.data.items;
    return [];
};

const pickSingle = (payload: any): any => {
    if (!payload || typeof payload !== 'object') return null;
    if (payload.data && typeof payload.data === 'object') return payload.data;
    return payload;
};

export interface ContactListQueryOptions {
    channelTypes?: ContactChannelType[];
    pageSize?: number;
}

export const contactService = {
    getContacts: async (options?: ContactListQueryOptions): Promise<Contact[]> => {
        const params = new URLSearchParams();
        params.set('pageSize', String(options?.pageSize ?? 200));
        options?.channelTypes?.forEach(type => params.append('channelTypes', toApiChannelType(type)));
        const query = params.toString();
        const payload = await apiRequest<any>(`/api/v1/contacts${query ? `?${query}` : ''}`);
        return pickItems(payload).map(normalizeContactWithDerivedChannels);
    },

    /** Resolves names for contacts that fall outside the paged list, so callers never render a raw id. */
    getContactsByIds: async (ids: string[]): Promise<Contact[]> => {
        const unique = Array.from(new Set(ids.map(id => id.trim()).filter(Boolean)));
        if (unique.length === 0) {
            return [];
        }

        const loaded = await Promise.all(unique.map(id => contactService.getContactById(id).catch(() => null)));
        return loaded.filter((contact): contact is Contact => contact !== null);
    },

    getContactMetadata: async (): Promise<ContactMetadata> => {
        const payload = await apiRequest<any>('/api/v1/contacts/metadata');
        return normalizeContactMetadata(pickSingle(payload));
    },

    getContactById: async (id: string): Promise<Contact | null> => {
        try {
            const payload = await apiRequest<any>(`/api/v1/contacts/${id}`);
            const item = pickSingle(payload);
            return item ? normalizeContactWithDerivedChannels(item) : null;
        } catch (error) {
            if (error instanceof Error && /404|not found/i.test(error.message)) {
                return null;
            }
            throw error;
        }
    },

    updateContact: async (id: string, updates: UpdateContactRequest): Promise<Contact | null> => {
        try {
            const payloadBody: Record<string, unknown> = {
                name: updates.name,
                firstName: updates.firstName,
                lastName: updates.lastName,
                email: updates.email,
                title: updates.title,
                phoneCountryCode: updates.phoneCountryCode,
                phoneNumber: updates.phoneNumber,
                whatsApp: updates.whatsApp,
                status: updates.status,
                marketingEmails: updates.marketingEmails,
                language: updates.language,
                reminderPreference: updates.reminderPreference,
                ubn: updates.ubn,
                accountingId: updates.accountingId,
                channels: updates.channels?.map(channel => ({
                    id: channel.id,
                    type: toApiChannelType(channel.type),
                    label: channel.label,
                    value: channel.value,
                    isPrimary: channel.isPrimary,
                })),
            };
            const payload = await apiRequest<any>(`/api/v1/contacts/${id}`, {
                method: 'PATCH',
                body: JSON.stringify(payloadBody),
            });
            const item = pickSingle(payload);
            return item ? normalizeContactWithDerivedChannels(item) : null;
        } catch (error) {
            if (error instanceof Error && /404|not found/i.test(error.message)) {
                return null;
            }
            throw error;
        }
    },

    createContact: async (request: CreateContactRequest): Promise<Contact | null> => {
        const payload = await apiRequest<any>('/api/v1/contacts', {
            method: 'POST',
            body: JSON.stringify({
                name: request.name,
                firstName: request.firstName,
                lastName: request.lastName,
                email: request.email,
                title: request.title,
                phoneCountryCode: request.phoneCountryCode,
                phoneNumber: request.phoneNumber,
                whatsApp: request.whatsApp,
                status: request.status,
                marketingEmails: request.marketingEmails,
                language: request.language,
                reminderPreference: request.reminderPreference,
                ubn: request.ubn,
                accountingId: request.accountingId,
                channels: request.channels?.map(channel => ({
                    id: channel.id,
                    type: toApiChannelType(channel.type),
                    label: channel.label,
                    value: channel.value,
                    isPrimary: channel.isPrimary,
                })),
            }),
        });
        const item = pickSingle(payload);
        return item ? normalizeContactWithDerivedChannels(item) : null;
    }
};

export const getContacts = contactService.getContacts;
export const getContactsByIds = contactService.getContactsByIds;
export const getContactMetadata = contactService.getContactMetadata;
export const getContactById = contactService.getContactById;
export const updateContact = contactService.updateContact;
export const createContact = contactService.createContact;